import { ITEM_TYPES } from "../../utils/hobblivision/itemTypes";
import { CARD_LAYOUTS, DEFAULT_CARD_LAYOUT } from "../../utils/hobblivision/cardRegions";
import { resolveCardRegions } from "../../utils/hobblivision/regionUtils";

/**
 * ============================================================
 * HobbliVision Collectible Detector
 * ============================================================
 *
 * VERSION 5.0
 *
 * PURPOSE
 * -------
 * V5 is a major architectural change from V4.
 * V4 was fundamentally:
 *     grayscale
 *       ↓
 *     edge map
 *       ↓
 *     rectangle search
 *       ↓
 *     rectangle scoring
 * That approach repeatedly demonstrated the same failure mode:
 *     "Find the strongest rectangle"
 * instead of:
 *     "Find the physical collectible."
 * V5 therefore changes the problem.
 *
 * PRIMARY ASSUMPTION
 * ------------------
 * The first production-oriented HobbliVision use case is:
 *     - one collectible
 *     - mostly centered
 *     - reasonably aligned
 *     - collectible occupies a meaningful portion of image
 *     - ordinary photograph/background
 * V5 does NOT attempt to solve:
 *     - arbitrary multi-object scenes
 *     - severe perspective distortion
 *     - heavily occluded objects
 *     - automatic identification
 *     - OCR
 *
 * Those are later stages.
 *
 * V5 PIPELINE
 * -----------
 *     ORIGINAL IMAGE
 *          ↓
 *     NORMALIZE
 *          ↓
 *     ANALYSIS IMAGE
 *          ↓
 *     FOREGROUND / CONTOUR ANALYSIS
 *          ↓
 *     LARGE OBJECT CANDIDATES
 *          ↓
 *     GEOMETRY + SIZE + CENTRALITY
 *          ↓
 *     CARD / SLAB RELATIONSHIP
 *          ↓
 *     BEST OBJECT
 *          ↓
 *     BOUNDARY REFINEMENT
 *          ↓
 *     ROI
 *          ↓
 *     CLASSIFICATION
 *
 * IMPORTANT DESIGN DECISION
 * -------------------------
 * Edges are now SUPPORTING evidence.   They are NOT the mechanism that creates the candidate.
 *
 * This is specifically intended to prevent an internal card,
 * label, face, sticky note, or artwork rectangle from winning
 * simply because it has stronger edges than a transparent slab.
 *
 * V5 DEBUGGING
 * ------------
 *
 * The detector deliberately returns rich diagnostics.
 * boundary.analysis includes:
 *     method
 *     opencvAvailable
 *     analysis dimensions
 *     candidate count
 *     selected candidate
 *     candidate scores
 *     stage information
 *
 * boundary.debugImages includes:
 *
 *     normalized image
 *     foreground mask
 *     contour visualization
 *     candidate visualization
 *     selected ROI visualization
 *
 * This is intentional.
 *
 * We want the Inspector to let us see WHAT the detector saw,
 * not merely the final red rectangle.
 *
 * FUTURE V5.x / V6 WORK
 * ---------------------
 *
 *     - perspective correction
 *     - rotated rectangles
 *     - better slab-specific detection
 *     - PSA / SGC / BGS / CGC geometry
 *     - multiple-object detection
 *     - learned object detection
 *     - ML-assisted candidate ranking
 *
 * ============================================================
 */


/**
 * ============================================================
 * CONFIGURATION
 * ============================================================
 */

const DETECTOR_VERSION = "5.0";

const CONFIG = {
    /**
     * Maximum image dimension used for computer vision.
     * The original image remains untouched.
     */
    MAX_ANALYSIS_SIZE: 1200,

    /**
     * Candidate requirements.
     * These are intentionally tuned for the first real-world
     * HobbliVision use case:
     * one large collectible in a reasonably aligned photo.
     */
    MIN_AREA_FRACTION: 0.035,

    IDEAL_MIN_AREA_FRACTION: 0.10,

    IDEAL_MAX_AREA_FRACTION: 0.80,

    /**
     * Extremely narrow or extremely wide contours are unlikely
     * to be the primary collectible.
     */
    MIN_ASPECT_RATIO: 0.35,

    MAX_ASPECT_RATIO: 2.50,

    /**
     * We prefer objects reasonably close to the image center,
     * but centrality is deliberately NOT a hard requirement.
     */
    CENTER_PREFERENCE_STRENGTH: 0.18,

    /**
     * Candidate must have some geometric rectangularity.
     */
    MIN_RECTANGULARITY: 0.35,

    /**
     * Minimum confidence required to call the result detected.
     * This is intentionally lower than we might eventually want.
     * Early production goal:
     *     useful ROI > false "unknown"
     * Later we can tighten this.
     */
    DETECTION_THRESHOLD: 0.42,

    /**
     * Small padding added to the final ROI.
     * We WANT the first V5 ROI to be slightly generous.
     * OCR benefits from not clipping labels or edges.
     */
    ROI_PADDING_HORIZONTAL: 0.1,
    ROI_PADDING_VERTICAL: 0.025,

    /**
     * Morphological operations.
     */
    MORPH_KERNEL_SIZE: 7,

    MORPH_ITERATIONS: 2,

    /**
     * Contour approximation.
     */
    POLYGON_APPROX_FACTOR: 0.025,

    /**
     * Candidate ranking weights.
     * Notice:
     * There is no giant edge-score weight.
     * The candidate is primarily evaluated as an OBJECT.
     */
    WEIGHTS: {
        area: 0.24,
        rectangularity: 0.20,
        geometry: 0.16,
        centrality: 0.12,
        size: 0.12,
        aspect: 0.08,
        boundary: 0.08
    }
};

/**
 * ============================================================
 * PUBLIC ENTRY POINT
 * ============================================================
 */

export async function detectCollectible(imageData) {
    if (!imageData) {
        throw new Error(
            "No processed image supplied."
        );
    }

    const {
        width,
        height,
        orientation,
        processed
    } = imageData;

    if (!width || !height) {
        throw new Error(
            "Image dimensions unavailable."
        );
    }

    if (!processed) {
        throw new Error(
            "Processed image unavailable."
        );
    }

    const source = await getImageSource(
        processed,
        width,
        height
    );

    if (!source) {
        return createUnknownResult(
            imageData,
            "image-source-unavailable"
        );
    }

    const analysis = analyzeDimensions(
        source.width,
        source.height
    );

    /**
     * Try OpenCV first.
     * If OpenCV is unavailable for any reason, V5 falls back
     * to a browser-native object-localization pass.
     */
    let boundary;

    try {
        if (isOpenCVAvailable()) {
            boundary = await detectWithOpenCV(
                source,
                analysis
            );
        } else {
            boundary = await detectWithNativeVision(
                source,
                analysis
            );
        }
    } catch (error) {
        console.warn(
            "HobbliVision V5 vision pass failed. Falling back to native vision.",
            error
        );
        boundary = await detectWithNativeVision(
            source,
            analysis
        );
    }

    const classificationAspectRatio =
        boundary.detected &&
        boundary.boundingBox
            ? boundary.boundingBox.width /
              boundary.boundingBox.height
            : null;

    const classification =
        classifyItem(
            classificationAspectRatio,
            boundary.detected
        );

    const regions =
        boundary.detected
            ? resolveCardRegions(
                boundary,
                classification.layout
            )
            : null;

    const confidence =
        boundary.detected
            ? Number(
                  (
                      boundary.confidence * 0.85 +
                      classification.confidence * 0.15
                  ).toFixed(3)
              )
            : 0;

    return {
        detectorVersion: DETECTOR_VERSION,
        itemType: classification.itemType,
        confidence,
        classificationConfidence:
            Number(
                classification.confidence.toFixed(3)
            ),
        width,
        height,
        orientation,
        aspectRatio:
            Number(
                (
                    width /
                    height
                ).toFixed(3)
            ),

        collectibleAspectRatio:
            classificationAspectRatio !== null
                ? Number(
                      classificationAspectRatio.toFixed(3)
                  )
                : null,
        layout:
            classification.layout,
        regions,
        boundary,
        image:
            processed,
        analysis
    };
}

/**
 * ============================================================
 * OPENCV AVAILABILITY
 * ============================================================
 */

function isOpenCVAvailable() {
    return (
        typeof window !== "undefined" &&
        !!window.cv &&
        typeof window.cv.Mat === "function"
    );
}

/**
 * ============================================================
 * OPENCV DETECTOR
 * ============================================================
 * This is the primary V5 detector.
 * IMPORTANT:
 * We do NOT simply ask OpenCV:
 *     "give me rectangles"
 * Instead we:
 *     1. create a useful foreground representation
 *     2. find connected contours
 *     3. filter candidates by physical plausibility
 *     4. rank candidates
 *     5. refine the chosen ROI
 *
 * This distinction is critical.
 */

async function detectWithOpenCV(
    source,
    analysis
) {
    const cv =
        window.cv;

    const canvas =
        document.createElement(
            "canvas"
        );

    canvas.width =
        analysis.analysisWidth;

    canvas.height =
        analysis.analysisHeight;

    const context =
        canvas.getContext(
            "2d",
            {
                willReadFrequently: true
            }
        );

    if (!context) {
        return createFallbackBoundary(
            source.width,
            source.height,
            "opencv-canvas-unavailable"
        );
    }

    context.drawImage(
        source.element,
        0,
        0,
        analysis.analysisWidth,
        analysis.analysisHeight
    );

    let sourceMat = null;
    let grayMat = null;
    let blurredMat = null;
    let edgeMat = null;
    let thresholdMat = null;
    let morphMat = null;
    let hierarchy = null;
    let contours = null;

    try {
        sourceMat = cv.imread(canvas);
        grayMat = new cv.Mat();
        blurredMat = new cv.Mat();
        edgeMat = new cv.Mat();
        thresholdMat = new cv.Mat();
        morphMat = new cv.Mat();
        hierarchy = new cv.Mat();
        contours = new cv.MatVector();

        /**
         * --------------------------------------------
         * GRAYSCALE
         * --------------------------------------------
         */

        cv.cvtColor(
            sourceMat,
            grayMat,
            cv.COLOR_RGBA2GRAY
        );

        /**
         * --------------------------------------------
         * LIGHT BLUR
         * --------------------------------------------
         *
         * We intentionally blur before edge/threshold work.
         * This suppresses:
         *     tiny text
         *     facial details
         *     card artwork details
         *     random photographic noise
         * while preserving large object structure.
         */

        cv.GaussianBlur(
            grayMat,
            blurredMat,
            new cv.Size(5, 5),
            0,
            0,
            cv.BORDER_DEFAULT
        );

        /**
         * --------------------------------------------
         * EDGE MAP
         * --------------------------------------------
         * Edge information is now supporting evidence.
         */

        cv.Canny(
            blurredMat,
            edgeMat,
            45,
            130
        );

        /**
         * --------------------------------------------
         * THRESHOLD
         * --------------------------------------------
         * Adaptive threshold gives us another representation
         * of large foreground structure.
         */

        cv.adaptiveThreshold(
            blurredMat,
            thresholdMat,
            255,
            cv.ADAPTIVE_THRESH_GAUSSIAN_C,
            cv.THRESH_BINARY,
            31,
            7
        );

        /**
         * --------------------------------------------
         * MORPHOLOGY
         * --------------------------------------------
         * Close gaps.
         *
         * This is particularly important for slabs because
         * their physical boundary can be visually interrupted
         * by glare/reflection.
         */

        const kernel =
            cv.getStructuringElement(
                cv.MORPH_RECT,
                new cv.Size(
                    CONFIG.MORPH_KERNEL_SIZE,
                    CONFIG.MORPH_KERNEL_SIZE
                )
            );

        cv.morphologyEx(
            edgeMat,
            morphMat,
            cv.MORPH_CLOSE,
            kernel,
            new cv.Point(-1, -1),
            CONFIG.MORPH_ITERATIONS
        );
        kernel.delete();

        /**
         * --------------------------------------------
         * CONTOURS
         * --------------------------------------------
         */

        cv.findContours(
            morphMat,
            contours,
            hierarchy,
            cv.RETR_EXTERNAL,
            cv.CHAIN_APPROX_SIMPLE
        );

        const candidates = [];
        const contourCount =
            contours.size();
        for (
            let i = 0;
            i < contourCount;
            i++
        ) {
            const contour =
                contours.get(i);
            try {
                const candidate =
                    evaluateOpenCVContour(
                        contour,
                        analysis.analysisWidth,
                        analysis.analysisHeight,
                        cv
                    );
                if (candidate) {
                    candidates.push(
                        candidate
                    );
                }
            } finally {
                contour.delete();
            }
        }

        /**
         * --------------------------------------------
         * SORT CANDIDATES
         * --------------------------------------------
         */

        candidates.sort(
            (a, b) =>
                b.score -
                a.score
        );

        /**
         * --------------------------------------------
         * VISUAL DEBUG IMAGES
         * --------------------------------------------
         */

        const contourVisualization =
            createContourVisualization(
                sourceMat,
                contours,
                cv
            );

        const selected =
            candidates.length
                ? candidates[0]
                : null;

        const candidateVisualization =
            createCandidateVisualization(
                sourceMat,
                candidates,
                selected,
                cv
            );

        /**
         * --------------------------------------------
         * NO CANDIDATE
         * --------------------------------------------
         */

        if (!selected) {
            return createFallbackBoundary(
                source.width,
                source.height,
                "no-plausible-object-contour",
                {
                    method:
                        "opencv-contour-v5",

                    opencvAvailable:
                        true,

                    analysisWidth:
                        analysis.analysisWidth,

                    analysisHeight:
                        analysis.analysisHeight,

                    candidateCount:
                        0,

                    debugImages: {
                        normalized:
                            canvas.toDataURL(
                                "image/jpeg",
                                0.85
                            ),

                        edgeMap:
                            matToDataURL(
                                edgeMat
                            ),

                        threshold:
                            matToDataURL(
                                thresholdMat
                            ),

                        morphology:
                            matToDataURL(
                                morphMat
                            ),

                        contours:
                            contourVisualization
                    }
                }
            );
        }

        /**
         * --------------------------------------------
         * REJECT VERY WEAK WINNER
         * --------------------------------------------
         */

        if (
            selected.score <
            CONFIG.DETECTION_THRESHOLD
        ) {
            return createFallbackBoundary(
                source.width,
                source.height,
                "best-contour-below-confidence-threshold",
                {
                    method:
                        "opencv-contour-v5",

                    opencvAvailable:
                        true,

                    analysisWidth:
                        analysis.analysisWidth,

                    analysisHeight:
                        analysis.analysisHeight,

                    candidateCount:
                        candidates.length,

                    selectedCandidate:
                        selected,

                    debugImages: {
                        normalized:
                            canvas.toDataURL(
                                "image/jpeg",
                                0.85
                            ),

                        edgeMap:
                            matToDataURL(
                                edgeMat
                            ),

                        threshold:
                            matToDataURL(
                                thresholdMat
                            ),

                        morphology:
                            matToDataURL(
                                morphMat
                            ),

                        contours:
                            contourVisualization,

                        candidates:
                            candidateVisualization
                    }
                }
            );
        }

        /**
         * --------------------------------------------
         * REFINE / PAD ROI
         * --------------------------------------------
         */

        const refined =
            refineObjectBoundingBox(
                selected.boundingBox,
                analysis.analysisWidth,
                analysis.analysisHeight
            );

        const padded =
            addROIPadding(
                refined,
                analysis.analysisWidth,
                analysis.analysisHeight
            );

        const sourceBox =
            convertAnalysisBoxToSource(
                padded,
                analysis.scale
            );

        const safeBox =
            clampBoundingBox(
                sourceBox,
                source.width,
                source.height
            );

        const croppedImage =
            createCrop(
                source.element,
                safeBox
            );

        const selectedVisualization =
            createSelectedVisualization(
                sourceMat,
                selected,
                padded,
                cv
            );

        return {
            detected:
                true,

            confidence:
                Number(
                    selected.score.toFixed(3)
                ),

            method:
                "opencv-object-localization-v5",

            boundingBox:
                safeBox,

            croppedImage,

            analysis: {
                detectorVersion:
                    DETECTOR_VERSION,

                method:
                    "opencv-object-localization-v5",

                opencvAvailable:
                    true,

                originalWidth:
                    source.width,

                originalHeight:
                    source.height,

                analysisWidth:
                    analysis.analysisWidth,

                analysisHeight:
                    analysis.analysisHeight,

                scale:
                    analysis.scale,

                candidateCount:
                    candidates.length,

                selectedCandidate:
                    serializeCandidate(
                        selected
                    ),

                candidates:
                    candidates
                        .slice(
                            0,
                            10
                        )
                        .map(
                            serializeCandidate
                        ),

                debugImages: {
                    normalized:
                        canvas.toDataURL(
                            "image/jpeg",
                            0.85
                        ),

                    edgeMap:
                        matToDataURL(
                            edgeMat
                        ),

                    threshold:
                        matToDataURL(
                            thresholdMat
                        ),

                    morphology:
                        matToDataURL(
                            morphMat
                        ),
                    contours:
                        contourVisualization,
                    candidates:
                        candidateVisualization,
                    selected:
                        selectedVisualization
                }
            }
        };
    } finally {
        if (sourceMat) {
            sourceMat.delete();
        }

        if (grayMat) {
            grayMat.delete();
        }

        if (blurredMat) {
            blurredMat.delete();
        }

        if (edgeMat) {
            edgeMat.delete();
        }

        if (thresholdMat) {
            thresholdMat.delete();
        }

        if (morphMat) {
            morphMat.delete();
        }

        if (hierarchy) {
            hierarchy.delete();
        }

        if (contours) {
            contours.delete();
        }
    }
}


/**
 * ============================================================
 * CONTOUR EVALUATION
 * ============================================================
 */

function evaluateOpenCVContour(
    contour,
    imageWidth,
    imageHeight,
    cv
) {
    const bounding =
        cv.boundingRect(
            contour
        );

    const area =
        cv.contourArea(
            contour
        );

    if (
        !Number.isFinite(area) ||
        area <= 0
    ) {
        return null;
    }

    const imageArea =
        imageWidth *
        imageHeight;

    const areaFraction =
        area /
        imageArea;

    if (
        areaFraction <
        CONFIG.MIN_AREA_FRACTION
    ) {
        return null;
    }

    if (
        bounding.width <
            imageWidth * 0.10 ||
        bounding.height <
            imageHeight * 0.10
    ) {
        return null;
    }

    const aspectRatio =
        bounding.width /
        bounding.height;

    if (
        aspectRatio <
            CONFIG.MIN_ASPECT_RATIO ||
        aspectRatio >
            CONFIG.MAX_ASPECT_RATIO
    ) {
        return null;
    }

    /**
     * --------------------------------------------
     * RECTANGULARITY
     * --------------------------------------------
     *
     * How much of the bounding rectangle is actually
     * occupied by the contour.
     */

    const boundingArea =
        bounding.width *
        bounding.height;

    const rectangularity =
        boundingArea > 0
            ? clamp(
                  area /
                      boundingArea,
                  0,
                  1
              )
            : 0;

    if (
        rectangularity <
        CONFIG.MIN_RECTANGULARITY
    ) {
        return null;
    }

    /**
     * --------------------------------------------
     * APPROXIMATE POLYGON
     * --------------------------------------------
     */
    const perimeter =
        cv.arcLength(
            contour,
            true
        );
    const approx =
        new cv.Mat();
    let vertexCount = 0;
    try {
        cv.approxPolyDP(
            contour,
            approx,
            perimeter *
                CONFIG.POLYGON_APPROX_FACTOR,
            true
        );
        vertexCount =
            approx.rows ||
            approx.size().height ||
            0;
    } finally {
        approx.delete();
    }

    /**
     * Four-sided shapes are strongly preferred.
     *
     * But we do not require exactly four points because:
     *
     *     - slabs may have reflections
     *     - photographs may have imperfect contours
     *     - rounded corners may create extra points
     */

    const geometryScore =
        calculateGeometryScore(
            vertexCount,
            rectangularity
        );

    /**
     * --------------------------------------------
     * AREA SCORE
     * --------------------------------------------
     */

    const areaScore =
        calculateAreaScore(
            areaFraction
        );

    /**
     * --------------------------------------------
     * SIZE SCORE
     * --------------------------------------------
     */

    const sizeScore =
        calculateSizeScore(
            bounding,
            imageWidth,
            imageHeight
        );

    /**
     * --------------------------------------------
     * CENTRALITY
     * --------------------------------------------
     */

    const centralityScore =
        calculateCentralityScore(
            bounding,
            imageWidth,
            imageHeight
        );

    /**
     * --------------------------------------------
     * ASPECT SCORE
     * --------------------------------------------
     */

    const aspectScore =
        calculateCollectibleAspectScore(
            aspectRatio
        );

    /**
     * --------------------------------------------
     * BOUNDARY SCORE
     * --------------------------------------------
     *
     * We still care whether the candidate has a meaningful
     * perimeter.
     *
     * But this is no longer the primary candidate generator.
     */

    const boundaryScore =
        calculateContourBoundaryScore(
            perimeter,
            bounding
        );

    /**
     * --------------------------------------------
     * TOTAL SCORE
     * --------------------------------------------
     */

    const score =
        areaScore *
            CONFIG.WEIGHTS.area +

        rectangularity *
            CONFIG.WEIGHTS.rectangularity +

        geometryScore *
            CONFIG.WEIGHTS.geometry +

        centralityScore *
            CONFIG.WEIGHTS.centrality +

        sizeScore *
            CONFIG.WEIGHTS.size +

        aspectScore *
            CONFIG.WEIGHTS.aspect +

        boundaryScore *
            CONFIG.WEIGHTS.boundary;

    return {
        x:
            bounding.x,

        y:
            bounding.y,

        width:
            bounding.width,

        height:
            bounding.height,

        boundingBox: {
            x:
                bounding.x,

            y:
                bounding.y,

            width:
                bounding.width,

            height:
                bounding.height
        },

        area,

        areaFraction,

        aspectRatio,

        rectangularity,

        vertexCount,

        geometryScore,

        areaScore,

        sizeScore,

        centralityScore,

        aspectScore,

        boundaryScore,

        score
    };
}


/**
 * ============================================================
 * GEOMETRY SCORING
 * ============================================================
 */

function calculateGeometryScore(
    vertexCount,
    rectangularity
) {
    let vertexScore;

    if (
        vertexCount === 4
    ) {
        vertexScore = 1;
    } else if (
        vertexCount === 5 ||
        vertexCount === 6
    ) {
        vertexScore = 0.85;
    } else if (
        vertexCount === 3 ||
        vertexCount === 7
    ) {
        vertexScore = 0.65;
    } else if (
        vertexCount === 8 ||
        vertexCount === 9
    ) {
        vertexScore = 0.55;
    } else {
        vertexScore = 0.40;
    }

    return clamp(
        vertexScore * 0.60 +
            rectangularity * 0.40,
        0,
        1
    );
}


/**
 * ============================================================
 * AREA SCORING
 * ============================================================
 */

function calculateAreaScore(
    areaFraction
) {
    if (
        areaFraction >=
            0.18 &&
        areaFraction <=
            0.70
    ) {
        return 1;
    }

    if (
        areaFraction >=
            CONFIG.IDEAL_MIN_AREA_FRACTION &&
        areaFraction <
            0.18
    ) {
        return lerp(
            0.78,
            1,
            (
                areaFraction -
                CONFIG.IDEAL_MIN_AREA_FRACTION
            ) /
                (
                    0.18 -
                    CONFIG.IDEAL_MIN_AREA_FRACTION
                )
        );
    }

    if (
        areaFraction >
            0.70 &&
        areaFraction <=
            CONFIG.IDEAL_MAX_AREA_FRACTION
    ) {
        return lerp(
            1,
            0.80,
            (
                areaFraction -
                0.70
            ) /
                0.10
        );
    }

    if (
        areaFraction >=
        CONFIG.MIN_AREA_FRACTION
    ) {
        return 0.55;
    }

    return 0;
}


/**
 ============================================================
 * SIZE SCORE
 * ============================================================
 */

function calculateSizeScore(
    bounding,
    imageWidth,
    imageHeight
) {
    const widthFraction =
        bounding.width /
        imageWidth;

    const heightFraction =
        bounding.height /
        imageHeight;

    /**
     * A typical card/slab photograph should occupy
     * a substantial portion of the frame.
     */

    const widthScore =
        clamp(
            widthFraction /
                0.60,
            0,
            1
        );

    const heightScore =
        clamp(
            heightFraction /
                0.85,
            0,
            1
        );

    return (
        widthScore *
            0.55 +
        heightScore *
            0.45
    );
}


/**
 * ============================================================
 * CENTRALITY SCORE
 * ============================================================
 */

function calculateCentralityScore(
    bounding,
    imageWidth,
    imageHeight
) {
    const centerX =
        bounding.x +
        bounding.width / 2;

    const centerY =
        bounding.y +
        bounding.height / 2;

    const imageCenterX =
        imageWidth / 2;

    const imageCenterY =
        imageHeight / 2;

    const dx =
        (
            centerX -
            imageCenterX
        ) /
        imageWidth;

    const dy =
        (
            centerY -
            imageCenterY
        ) /
        imageHeight;

    const distance =
        Math.sqrt(
            dx * dx +
            dy * dy
        );

    return clamp(
        1 -
            distance *
                2.0,
        0.30,
        1
    );
}


/**
 * ============================================================
 * COLLECTIBLE ASPECT SCORE
 * ============================================================
 */

function calculateCollectibleAspectScore(
    aspectRatio
) {
    const expected =
        [
            0.55,
            0.60,
            0.625,
            0.667,
            0.714,
            0.75,
            0.80,
            1.00,
            1.20,
            1.333,
            1.50,
            1.67,
            1.80
        ];

    let closest =
        Infinity;

    for (
        const ratio of
        expected
    ) {
        closest =
            Math.min(
                closest,
                Math.abs(
                    aspectRatio -
                        ratio
                )
            );
    }

    return clamp(
        1 -
            closest /
                0.45,
        0,
        1
    );
}


/**
 * ============================================================
 * CONTOUR BOUNDARY SCORE
 * ============================================================
 */

function calculateContourBoundaryScore(
    perimeter,
    bounding
) {
    const rectanglePerimeter =
        2 *
        (
            bounding.width +
            bounding.height
        );

    if (
        rectanglePerimeter <= 0
    ) {
        return 0;
    }

    /**
     * A contour perimeter close to the bounding rectangle's
     * perimeter is useful evidence of a coherent outer object.
     */

    return clamp(
        perimeter /
            rectanglePerimeter,
        0,
        1
    );
}


/**
 * ============================================================
 * ROI REFINEMENT
 * ============================================================
 *
 * V5 intentionally makes the final ROI slightly generous.
 *
 * We would rather give OCR:
 *
 *     3% too much image
 *
 * than:
 *
 *     crop off the PSA label.
 */

function refineObjectBoundingBox(
    boundingBox,
    imageWidth,
    imageHeight
) {
    return clampBoundingBox(
        boundingBox,
        imageWidth,
        imageHeight
    );
}


function addROIPadding(
    boundingBox,
    imageWidth,
    imageHeight
) {
    const paddingX =
        Math.round(
            boundingBox.width *
                CONFIG.ROI_PADDING_HORIZONTAL
        );

    const paddingY =
        Math.round(
            boundingBox.height *
                CONFIG.ROI_PADDING_VERTICAL
        );

    return clampBoundingBox(
        {
            x:
                boundingBox.x -
                paddingX,
            y:
                boundingBox.y -
                paddingY,
            width:
                boundingBox.width +
                paddingX * 2,
            height:
                boundingBox.height +
                paddingY * 2
        },
        imageWidth,
        imageHeight
    );
}


/**
 * ============================================================
 * NATIVE BROWSER FALLBACK
 * ============================================================
 *
 * This is NOT intended to compete with OpenCV.
 *
 * Its purpose is:
 *
 *     - prevent HobbliVision from crashing
 *     - keep development moving
 *     - provide a useful fallback if OpenCV hasn't loaded
 *
 * The fallback uses a simplified large-object analysis.
 */

async function detectWithNativeVision(
    source,
    analysis
) {
    const canvas =
        document.createElement(
            "canvas"
        );

    canvas.width =
        analysis.analysisWidth;

    canvas.height =
        analysis.analysisHeight;

    const context =
        canvas.getContext(
            "2d",
            {
                willReadFrequently: true
            }
        );

    if (!context) {
        return createFallbackBoundary(
            source.width,
            source.height,
            "native-canvas-unavailable"
        );
    }

    context.drawImage(
        source.element,
        0,
        0,
        analysis.analysisWidth,
        analysis.analysisHeight
    );

    const pixels =
        context.getImageData(
            0,
            0,
            analysis.analysisWidth,
            analysis.analysisHeight
        );

    const grayscale =
        createGrayscaleImage(
            pixels
        );

    const edges =
        calculateEdgeMap(
            grayscale,
            analysis.analysisWidth,
            analysis.analysisHeight
        );

    /**
     * Instead of scanning hundreds of arbitrary rectangles like V4,
     * generate only a modest set of LARGE, CENTERED candidate regions.
     *
     * This is a fallback only.
     */

    const candidates =
        generateNativeCandidates(
            analysis.analysisWidth,
            analysis.analysisHeight
        );

    const evaluated =
        candidates
            .map(
                candidate =>
                    evaluateNativeCandidate(
                        candidate,
                        edges,
                        analysis.analysisWidth,
                        analysis.analysisHeight
                    )
            )
            .filter(Boolean)
            .sort(
                (a, b) =>
                    b.score -
                    a.score
            );

    const selected =
        evaluated[0];

    if (
        !selected ||
        selected.score <
            CONFIG.DETECTION_THRESHOLD
    ) {
        return createFallbackBoundary(
            source.width,
            source.height,
            "native-no-plausible-object"
        );
    }

    const padded =
        addROIPadding(
            selected.boundingBox,
            analysis.analysisWidth,
            analysis.analysisHeight
        );

    const sourceBox =
        convertAnalysisBoxToSource(
            padded,
            analysis.scale
        );

    const safeBox =
        clampBoundingBox(
            sourceBox,
            source.width,
            source.height
        );

    return {
        detected:
            true,

        confidence:
            Number(
                selected.score.toFixed(3)
            ),

        method:
            "native-object-localization-v5-fallback",

        boundingBox:
            safeBox,

        croppedImage:
            createCrop(
                source.element,
                safeBox
            ),

        analysis: {
            detectorVersion:
                DETECTOR_VERSION,

            method:
                "native-object-localization-v5-fallback",

            opencvAvailable:
                false,

            candidateCount:
                evaluated.length,

            selectedCandidate:
                selected
        }
    };
}


/**
 * ============================================================
 * NATIVE CANDIDATE GENERATION
 * ============================================================
 */

function generateNativeCandidates(
    width,
    height
) {
    const candidates = [];

    const widthFractions =
        [
            0.40,
            0.50,
            0.60,
            0.70
        ];

    const aspectRatios =
        [
            0.55,
            0.625,
            0.667,
            0.714,
            0.75,
            0.80,
            1.0
        ];

    /**
     * Candidate positions are deliberately concentrated
     * around the center of the image.
     */

    const xOffsets =
        [
            -0.12,
            -0.06,
            0,
            0.06,
            0.12
        ];

    const yOffsets =
        [
            -0.10,
            -0.05,
            0,
            0.05,
            0.10
        ];

    for (
        const widthFraction of
        widthFractions
    ) {
        for (
            const aspectRatio of
            aspectRatios
        ) {
            const candidateWidth =
                Math.round(
                    width *
                        widthFraction
                );

            const candidateHeight =
                Math.round(
                    candidateWidth /
                        aspectRatio
                );

            if (
                candidateWidth >=
                    width ||
                candidateHeight >=
                    height
            ) {
                continue;
            }

            for (
                const xOffset of
                xOffsets
            ) {
                for (
                    const yOffset of
                    yOffsets
                ) {
                    const centerX =
                        width / 2 +
                        width *
                            xOffset;

                    const centerY =
                        height / 2 +
                        height *
                            yOffset;

                    candidates.push(
                        {
                            x:
                                Math.round(
                                    centerX -
                                        candidateWidth /
                                            2
                                ),

                            y:
                                Math.round(
                                    centerY -
                                        candidateHeight /
                                            2
                                ),

                            width:
                                candidateWidth,

                            height:
                                candidateHeight
                        }
                    );
                }
            }
        }
    }

    return candidates;
}


/**
 * ============================================================
 * NATIVE CANDIDATE EVALUATION
 * ============================================================
 */

function evaluateNativeCandidate(
    candidate,
    edges,
    imageWidth,
    imageHeight
) {
    const areaFraction =
        (
            candidate.width *
            candidate.height
        ) /
        (
            imageWidth *
            imageHeight
        );

    if (
        areaFraction <
        CONFIG.MIN_AREA_FRACTION
    ) {
        return null;
    }

    const aspectRatio =
        candidate.width /
        candidate.height;

    const boundaryScore =
        scoreNativeBoundary(
            candidate,
            edges,
            imageWidth,
            imageHeight
        );

    const sizeScore =
        calculateSizeScore(
            candidate,
            imageWidth,
            imageHeight
        );

    const centralityScore =
        calculateCentralityScore(
            candidate,
            imageWidth,
            imageHeight
        );

    const aspectScore =
        calculateCollectibleAspectScore(
            aspectRatio
        );

    const areaScore =
        calculateAreaScore(
            areaFraction
        );

    const score =
        areaScore * 0.30 +
        boundaryScore * 0.25 +
        sizeScore * 0.20 +
        centralityScore * 0.15 +
        aspectScore * 0.10;

    return {
        boundingBox:
            candidate,

        areaFraction,

        aspectRatio,

        boundaryScore,

        sizeScore,

        centralityScore,

        aspectScore,

        areaScore,

        score
    };
}


/**
 * ============================================================
 * NATIVE BOUNDARY SCORE
 * ============================================================
 */

function scoreNativeBoundary(
    rectangle,
    edges,
    width,
    height
) {
    const samples =
        30;

    let total =
        0;

    let count =
        0;

    const sampleSide = (
        side
    ) => {
        for (
            let i = 0;
            i < samples;
            i++
        ) {
            const progress =
                (
                    i +
                    0.5
                ) /
                samples;

            let x;
            let y;

            if (
                side ===
                "top"
            ) {
                x =
                    rectangle.x +
                    rectangle.width *
                        progress;

                y =
                    rectangle.y;
            } else if (
                side ===
                "bottom"
            ) {
                x =
                    rectangle.x +
                    rectangle.width *
                        progress;

                y =
                    rectangle.y +
                    rectangle.height;
            } else if (
                side ===
                "left"
            ) {
                x =
                    rectangle.x;

                y =
                    rectangle.y +
                    rectangle.height *
                        progress;
            } else {
                x =
                    rectangle.x +
                    rectangle.width;

                y =
                    rectangle.y +
                    rectangle.height *
                        progress;
            }

            const ix =
                Math.round(
                    x
                );

            const iy =
                Math.round(
                    y
                );

            if (
                isInside(
                    ix,
                    iy,
                    width,
                    height
                )
            ) {
                total +=
                    edges[
                        iy *
                            width +
                        ix
                    ];

                count++;
            }
        }
    };

    sampleSide("top");
    sampleSide("bottom");
    sampleSide("left");
    sampleSide("right");

    if (!count) {
        return 0;
    }

    return clamp(
        total /
            count,
        0,
        1
    );
}


/**
 * ============================================================
 * DEBUG VISUALIZATION
 * ============================================================
 */

function createContourVisualization(
    sourceMat,
    contours,
    cv
) {
    try {
        const visualization =
            sourceMat.clone();

        for (
            let i = 0;
            i < contours.size();
            i++
        ) {
            const contour =
                contours.get(i);

            try {
                const area =
                    cv.contourArea(
                        contour
                    );

                if (
                    area <=
                    0
                ) {
                    continue;
                }

                const color =
                    new cv.Scalar(
                        255,
                        200,
                        0,
                        255
                    );

                cv.drawContours(
                    visualization,
                    contours,
                    i,
                    color,
                    2
                );
            } finally {
                contour.delete();
            }
        }

        const result =
            matToDataURL(
                visualization
            );

        visualization.delete();

        return result;
    } catch (error) {
        console.warn(
            "HobbliVision could not create contour debug image.",
            error
        );

        return null;
    }
}


function createCandidateVisualization(
    sourceMat,
    candidates,
    selected,
    cv
) {
    try {
        const visualization =
            sourceMat.clone();

        candidates
            .slice(
                0,
                10
            )
            .forEach(
                (
                    candidate,
                    index
                ) => {
                    const rect =
                        new cv.Rect(
                            candidate.x,
                            candidate.y,
                            candidate.width,
                            candidate.height
                        );

                    const color =
                        selected ===
                        candidate
                            ? new cv.Scalar(
                                  0,
                                  255,
                                  0,
                                  255
                              )
                            : new cv.Scalar(
                                  255,
                                  180,
                                  0,
                                  255
                              );

                    cv.rectangle(
                        visualization,
                        rect,
                        color,
                        selected ===
                        candidate
                            ? 4
                            : 2
                    );

                    cv.putText(
                        visualization,
                        `${index + 1}: ${candidate.score.toFixed(
                            2
                        )}`,
                        new cv.Point(
                            candidate.x + 4,
                            Math.max(
                                18,
                                candidate.y +
                                    18
                            )
                        ),
                        cv.FONT_HERSHEY_SIMPLEX,
                        0.45,
                        color,
                        1
                    );
                }
            );

        const result =
            matToDataURL(
                visualization
            );

        visualization.delete();

        return result;
    } catch (error) {
        console.warn(
            "HobbliVision could not create candidate debug image.",
            error
        );

        return null;
    }
}


function createSelectedVisualization(
    sourceMat,
    selected,
    padded,
    cv
) {
    try {
        const visualization =
            sourceMat.clone();

        const selectedRect =
            new cv.Rect(
                selected.x,
                selected.y,
                selected.width,
                selected.height
            );

        const paddedRect =
            new cv.Rect(
                padded.x,
                padded.y,
                padded.width,
                padded.height
            );

        cv.rectangle(
            visualization,
            selectedRect,
            new cv.Scalar(
                0,
                255,
                0,
                255
            ),
            4
        );

        cv.rectangle(
            visualization,
            paddedRect,
            new cv.Scalar(
                255,
                255,
                0,
                255
            ),
            2
        );

        const result =
            matToDataURL(
                visualization
            );

        visualization.delete();

        return result;
    } catch (error) {
        console.warn(
            "HobbliVision could not create selected-object visualization.",
            error
        );

        return null;
    }
}


/**
 * ============================================================
 * MAT → DATA URL
 * ============================================================
 */

function matToDataURL(
    mat
) {
    try {
        const canvas =
            document.createElement(
                "canvas"
            );

        canvas.width =
            mat.cols;

        canvas.height =
            mat.rows;

        cvMatToCanvas(
            mat,
            canvas
        );

        return canvas.toDataURL(
            "image/jpeg",
            0.85
        );
    } catch (error) {
        console.warn(
            "HobbliVision could not convert OpenCV image to data URL.",
            error
        );

        return null;
    }
}


/**
 * ============================================================
 * OPENCV CANVAS HELPER
 * ============================================================
 */

function cvMatToCanvas(
    mat,
    canvas
) {
    const cv =
        window.cv;

    cv.imshow(
        canvas,
        mat
    );
}


/**
 * ============================================================
 * IMAGE SOURCE
 * ============================================================
 */

async function getImageSource(
    processed,
    width,
    height
) {
    if (!processed) {
        return null;
    }

    if (
        typeof HTMLImageElement !==
            "undefined" &&
        processed instanceof
            HTMLImageElement
    ) {
        return {
            element:
                processed,

            width:
                processed.naturalWidth ||
                processed.width ||
                width,

            height:
                processed.naturalHeight ||
                processed.height ||
                height
        };
    }

    if (
        typeof HTMLCanvasElement !==
            "undefined" &&
        processed instanceof
            HTMLCanvasElement
    ) {
        return {
            element:
                processed,

            width:
                processed.width,

            height:
                processed.height
        };
    }

    if (
        typeof ImageBitmap !==
            "undefined" &&
        processed instanceof
            ImageBitmap
    ) {
        return {
            element:
                processed,

            width:
                processed.width,

            height:
                processed.height
        };
    }

    if (
        typeof processed ===
        "string"
    ) {
        try {
            const image =
                await loadImage(
                    processed
                );

            return {
                element:
                    image,

                width:
                    image.naturalWidth,

                height:
                    image.naturalHeight
            };
        } catch (error) {
            console.warn(
                "HobbliVision V5 could not load processed image.",
                error
            );

            return null;
        }
    }

    return null;
}


function loadImage(
    url
) {
    return new Promise(
        (
            resolve,
            reject
        ) => {
            const image =
                new Image();

            image.onload =
                () =>
                    resolve(
                        image
                    );

            image.onerror =
                () =>
                    reject(
                        new Error(
                            "Unable to load image."
                        )
                    );

            image.src =
                url;
        }
    );
}


/**
 * ============================================================
 * GRAYSCALE
 * ============================================================
 */

function createGrayscaleImage(
    imageData
) {
    const {
        data,
        width,
        height
    } = imageData;

    const grayscale =
        new Float32Array(
            width *
                height
        );

    for (
        let i = 0;
        i <
        grayscale.length;
        i++
    ) {
        const pixel =
            i * 4;

        grayscale[i] =
            0.299 *
                data[pixel] +
            0.587 *
                data[pixel + 1] +
            0.114 *
                data[pixel + 2];
    }

    return grayscale;
}


/**
 * ============================================================
 * NATIVE EDGE MAP
 * ============================================================
 */

function calculateEdgeMap(
    grayscale,
    width,
    height
) {
    const edges =
        new Float32Array(
            width *
                height
        );

    for (
        let y = 1;
        y <
        height - 1;
        y++
    ) {
        for (
            let x = 1;
            x <
            width - 1;
            x++
        ) {
            const topLeft =
                grayscale[
                    (y - 1) *
                        width +
                    (x - 1)
                ];

            const top =
                grayscale[
                    (y - 1) *
                        width +
                    x
                ];

            const topRight =
                grayscale[
                    (y - 1) *
                        width +
                    (x + 1)
                ];

            const left =
                grayscale[
                    y *
                        width +
                    (x - 1)
                ];

            const right =
                grayscale[
                    y *
                        width +
                    (x + 1)
                ];

            const bottomLeft =
                grayscale[
                    (y + 1) *
                        width +
                    (x - 1)
                ];

            const bottom =
                grayscale[
                    (y + 1) *
                        width +
                    x
                ];

            const bottomRight =
                grayscale[
                    (y + 1) *
                        width +
                    (x + 1)
                ];

            const horizontal =
                -topLeft +
                topRight -
                2 * left +
                2 * right -
                bottomLeft +
                bottomRight;

            const vertical =
                -topLeft -
                2 * top -
                topRight +
                bottomLeft +
                2 * bottom +
                bottomRight;

            edges[
                y *
                    width +
                x
            ] =
                Math.sqrt(
                    horizontal *
                        horizontal +
                    vertical *
                        vertical
                );
        }
    }

    let max =
        1;

    for (
        let i = 0;
        i <
        edges.length;
        i++
    ) {
        max =
            Math.max(
                max,
                edges[i]
            );
    }

    for (
        let i = 0;
        i <
        edges.length;
        i++
    ) {
        edges[i] =
            edges[i] /
            max;
    }

    return edges;
}


/**
 * ============================================================
 * UNKNOWN / FALLBACK
 * ============================================================
 */

function createUnknownResult(
    imageData,
    reason
) {
    const {
        width,
        height,
        orientation,
        processed
    } = imageData;

    return {
        detectorVersion:
            DETECTOR_VERSION,

        itemType:
            ITEM_TYPES.UNKNOWN,

        confidence:
            0,

        classificationConfidence:
            0,

        width,

        height,

        orientation,

        aspectRatio:
            width &&
            height
                ? Number(
                      (
                          width /
                          height
                      ).toFixed(3)
                  )
                : null,

        collectibleAspectRatio:
            null,

        layout:
            DEFAULT_CARD_LAYOUT,

        boundary:
            createFallbackBoundary(
                width,
                height,
                reason
            ),

        image:
            processed,

        analysis:
            analyzeDimensions(
                width,
                height
            )
    };
}

function createFallbackBoundary(
    width,
    height,
    reason,
    extraAnalysis = {}
) {
    return {
        detected:
            false,
        confidence:
            0,
        method:
            "fallback-full-image-v5",
        boundingBox: {
            x: 0,
            y: 0,
            width,
            height
        },
        croppedImage:
            null,
        analysis: {
            detectorVersion:
                DETECTOR_VERSION,

            reason,

            ...extraAnalysis
        }
    };
}

/**
 * ============================================================
 * CLASSIFICATION
 * ============================================================
 *
 * This remains intentionally conservative.
 *
 * Detection answers:
 *
 *     "Where is the object?"
 *
 * Classification answers:
 *
 *     "What broad category does its geometry suggest?"
 *
 * OCR / matching will later answer:
 *
 *     "What exact collectible is it?"
 */

function classifyItem(
    aspectRatio,
    boundaryDetected = false
) {
    if (
        !boundaryDetected ||
        aspectRatio === null
    ) {
        return {
            itemType:
                ITEM_TYPES.UNKNOWN,
            confidence:
                0,
            layout:
                DEFAULT_CARD_LAYOUT
        };
    }

    if (
        isNear(
            aspectRatio,
            0.714,
            0.10
        )
    ) {
        return {
            itemType:
                ITEM_TYPES.CARD,
            confidence:
                0.90,
            layout: {
                name:
                    "RAW_CARD",

                ...CARD_LAYOUTS.RAW_CARD
            }
        };
    }

    if (
        isNear(
            aspectRatio,
            0.667,
            0.10
        )
    ) {
        return {
            itemType:
                ITEM_TYPES.COMIC,
            confidence:
                0.80,
            layout:
                DEFAULT_CARD_LAYOUT
        };
    }

    if (
        isNear(
            aspectRatio,
            1.0,
            0.10
        )
    ) {
        return {
            itemType:ITEM_TYPES.COIN,
            confidence:0.75,
            layout:DEFAULT_CARD_LAYOUT
        };
    }

    return {
        itemType:ITEM_TYPES.UNKNOWN,
        confidence:0.50,
        layout:DEFAULT_CARD_LAYOUT
    };
}

/**
 * ============================================================
 * DEBUG SERIALIZATION
 * ============================================================
 */

function serializeCandidate(
    candidate
) {
    if (!candidate) {
        return null;
    }

    return {
        boundingBox:
            candidate.boundingBox
                ? {
                      ...candidate.boundingBox
                  }
                : {
                      x:candidate.x,
                      y:candidate.y,
                      width:candidate.width,
                      height:candidate.height
                  },

        areaFraction:
            round3(
                candidate.areaFraction
            ),

        aspectRatio:
            round3(
                candidate.aspectRatio
            ),

        rectangularity:
            round3(
                candidate.rectangularity
            ),

        vertexCount:
            candidate.vertexCount,

        geometryScore:
            round3(
                candidate.geometryScore
            ),

        areaScore:
            round3(
                candidate.areaScore
            ),

        sizeScore:
            round3(
                candidate.sizeScore
            ),

        centralityScore:
            round3(
                candidate.centralityScore
            ),

        aspectScore:
            round3(
                candidate.aspectScore
            ),

        boundaryScore:
            round3(
                candidate.boundaryScore
            ),

        score:
            round3(
                candidate.score
            )
    };
}

/**
 * ============================================================
 * DIMENSIONS
 * ============================================================
 */

function analyzeDimensions(
    width,
    height
) {
    const scale =
        Math.min(
            1,
            CONFIG.MAX_ANALYSIS_SIZE /
                Math.max(
                    width,
                    height
                )
        );

    return {
        width,

        height,

        aspectRatio:
            Number(
                (
                    width /
                    height
                ).toFixed(3)
            ),

        scale,

        analysisWidth:
            Math.max(
                1,
                Math.round(
                    width *
                        scale
                )
            ),

        analysisHeight:
            Math.max(
                1,
                Math.round(
                    height *
                        scale
                )
            )
    };
}

/**
 * ============================================================
 * ANALYSIS → SOURCE COORDINATE CONVERSION
 * ============================================================
 */

function convertAnalysisBoxToSource(
    box,
    scale
) {
    const inverseScale =
        1 /
        Math.max(
            scale,
            0.0001
        );

    return {
        x:
            Math.round(
                box.x *
                    inverseScale
            ),

        y:
            Math.round(
                box.y *
                    inverseScale
            ),

        width:
            Math.round(
                box.width *
                    inverseScale
            ),

        height:
            Math.round(
                box.height *
                    inverseScale
            )
    };
}

/**
 * ============================================================
 * CROPPING
 * ============================================================
 */

function createCrop(
    image,
    boundingBox
) {
    try {
        const canvas =
            document.createElement(
                "canvas"
            );

        canvas.width =
            boundingBox.width;

        canvas.height =
            boundingBox.height;

        const context =
            canvas.getContext(
                "2d"
            );

        if (!context) {
            return null;
        }

        context.drawImage(
            image,
            boundingBox.x,
            boundingBox.y,
            boundingBox.width,
            boundingBox.height,
            0,
            0,
            boundingBox.width,
            boundingBox.height
        );

        return canvas.toDataURL(
            "image/jpeg",
            0.92
        );
    } catch (error) {
        console.warn(
            "HobbliVision V5 could not create crop.",
            error
        );

        return null;
    }
}

/**
 * ============================================================
 * BOUNDING BOX HELPERS
 * ============================================================
 */

function clampBoundingBox(
    boundingBox,
    imageWidth,
    imageHeight
) {
    const x =
        clamp(
            Math.round(
                boundingBox.x
            ),
            0,
            Math.max(
                0,
                imageWidth - 1
            )
        );

    const y =
        clamp(
            Math.round(
                boundingBox.y
            ),
            0,
            Math.max(
                0,
                imageHeight - 1
            )
        );

    const width =
        clamp(
            Math.round(
                boundingBox.width
            ),
            1,
            Math.max(
                1,
                imageWidth - x
            )
        );

    const height =
        clamp(
            Math.round(
                boundingBox.height
            ),
            1,
            Math.max(
                1,
                imageHeight - y
            )
        );

    return {
        x,
        y,
        width,
        height
    };
}

/**
 * ============================================================
 * HELPERS
 * ============================================================
 */

function clamp(
    value,
    min,
    max
) {
    return Math.max(
        min,
        Math.min(
            max,
            value
        )
    );
}

function lerp(
    a,
    b,
    t
) {
    return (
        a +
        (
            b - a
        ) *
            clamp(
                t,
                0,
                1
            )
    );
}

function round3(
    value
) {
    return Number(
        Number(
            value || 0
        ).toFixed(3)
    );
}

function isNear(
    value,
    target,
    tolerance
) {
    return (
        Math.abs(
            value -
                target
        ) <=
        tolerance
    );
}

function isInside(
    x,
    y,
    width,
    height
) {
    return (
        x >= 0 &&
        x < width &&
        y >= 0 &&
        y < height
    );
}

